import React, { memo } from 'react';
import type { ProgressBarKindProps } from '../../types';
import { Text } from '../../../Text';
import { Image } from '../../../Image';
import { resolveLabel } from '../../../../../utils/contentResolver';
import { CircleProgressBar } from '../shared';
import { createContainerHeightStyle, getMediaContainerHeightPercentage } from '../shared/containerHeight';
import styles from './ChartContainerFirstLayout.module.css';

export const ChartContainerFirstLayout = memo(function ChartContainerFirstLayout({
  stepper,
  progress,
  locale,
  currentSegment,
  currentSegmentIndex,
  labelStyle,
  hasLabelStyle,
  containerStyle,
  variables,
  previewMode,
  className,
  testId,
  editPath,
}: ProgressBarKindProps): React.ReactElement {
  const { appearance } = stepper;
  const mediaContainerHeight = getMediaContainerHeightPercentage(appearance);
  const chartContainerHeight = 100 - mediaContainerHeight;

  const image = currentSegment?.content?.image;

  const itemEditPath = editPath && currentSegmentIndex >= 0
    ? `${editPath}.items.${currentSegmentIndex}.content`
    : undefined;

  const circleWidthPercent = appearance?.dimensions?.width;

  const hasSubtitle = currentSegment?.content?.subtitle && resolveLabel(currentSegment.content.subtitle, locale) !== null;
  const hasDescription = currentSegment?.content?.description && resolveLabel(currentSegment.content.description, locale) !== null;

  return (
    <div
      className={className ? `${styles.container} ${className}` : styles.container}
      data-testid={testId}
      data-edit-path={editPath}
    >
      {/* TOP: Chart Container - Circle + Title */}
      <div
        className={styles.chartContainer}
        style={createContainerHeightStyle(chartContainerHeight)}
        data-testid="view-progress-steps-chart-container"
      >
        <div
          className={styles.circleWrapper}
          style={{
            ...(circleWidthPercent && { width: `${circleWidthPercent}%`, flex: '0 0 auto' }),
            ...containerStyle,
          }}
        >
          <CircleProgressBar
            progress={progress}
            appearance={appearance}
            labelStyle={labelStyle}
            hasLabelStyle={hasLabelStyle}
            testId={testId}
          />
        </div>

        {currentSegment?.content?.title && (
          <div className={styles.titleContainer}>
            <Text
              label={currentSegment.content.title}
              locale={locale}
              variables={variables}
              style={hasLabelStyle ? labelStyle : undefined}
              editPath={itemEditPath ? `${itemEditPath}.title` : undefined}
            />
          </div>
        )}
      </div>

      {/* BOTTOM: Media Container - Image + Subtitle/Description */}
      <div
        className={styles.mediaContainer}
        style={createContainerHeightStyle(mediaContainerHeight)}
        data-testid="view-progress-steps-media-container"
      >
        {image && (
          <div className={styles.imageGridContainer}>
            <Image
              image={image}
              locale={locale}
              className={styles.gridImage}
              previewMode={previewMode}
              fillContainer
              editPath={itemEditPath ? `${itemEditPath}.image` : undefined}
            />
          </div>
        )}

        {(hasSubtitle || hasDescription) && (
          <div className={styles.progressTextBox}>
            {currentSegment?.content?.subtitle && (
              <Text
                label={currentSegment.content.subtitle}
                locale={locale}
                variables={variables}
                style={hasLabelStyle ? labelStyle : undefined}
                editPath={itemEditPath ? `${itemEditPath}.subtitle` : undefined}
              />
            )}
            {currentSegment?.content?.description && (
              <Text
                label={currentSegment.content.description}
                locale={locale}
                variables={variables}
                style={hasLabelStyle ? labelStyle : undefined}
                editPath={itemEditPath ? `${itemEditPath}.description` : undefined}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
});

ChartContainerFirstLayout.displayName = 'ChartContainerFirstLayout';
